import { useCallback, useEffect, useMemo, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import * as yup from 'yup'

import { defaultCurrency } from 'shared/constants'
import { useAppTranslation } from 'shared/hooks/useAppTranslation'

import { selectUserCurrency, selectUserData } from 'features/auth/selectors'
import { getBonusHistory } from 'features/bonusHistory/actions'
import { selectBonusHistory, selectBonusHistoryComm } from 'features/bonusHistory/selectors'
import { exchangeBonusBalanceToBalance } from 'features/bonusSystem/actions'
import { selectSettings } from 'features/settings/selectors'

import { addNotify } from 'components/Notify'
import { usePagination } from 'components/UIKit'
import { getSelectOptionValue, SelectOption } from 'components/UIKit/Select'

export type ExchangeBonusHistoryFilters = {
  periodStart: Date
  periodEnd: Date
  type: SelectOption
  currency: SelectOption
}

type ExchangeBonusBalanceForm = {
  amount: string
}

export const MIN_BONUS_BALANCE_AMOUNT = 1

const ALL_OPTION_VALUE = 'all'
const BONUS_TYPE_OPTIONS = [
  ALL_OPTION_VALUE,
  'Cashback',
  'FirstDeposit',
  'TransferBonusesToBalance',
  'LuckySpin',
  'PromotionToRealBalance',
  'DecreasedByOwner',
  'IncreasedByOwner'
]

const validationSchema = yup.object().shape({
  amount: yup.string().required('required'),
})

export const useExchangeBonus = () => {
  const { count, history } = useSelector(selectBonusHistory, shallowEqual)
  const dispatch = useDispatch()
  const { t, tPrefix } = useAppTranslation('profile')
  const userCurrency = useSelector(selectUserCurrency, shallowEqual)
  const userInfo = useSelector(selectUserData, shallowEqual)
  const { isLoading, error, isSuccess } = useSelector(selectBonusHistoryComm, shallowEqual).getBonusHistory

  const bonusSettings = useSelector(selectSettings, shallowEqual).settings.bonuses
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 15,
    totalItems: count,
  })

  const fetchBonusHistory = useCallback((values: ExchangeBonusHistoryFilters) => {
    const typeValue = getSelectOptionValue(values.type)

    dispatch(
      getBonusHistory({
        page_size: pagination.pageSize,
        page: pagination.page,
        date_from: values.periodStart,
        date_to: values.periodEnd,
        type: typeValue === ALL_OPTION_VALUE ? undefined : typeValue,
      }),
    )
  }, [dispatch, pagination.page, pagination.pageSize])

  const typeOptions = useMemo<SelectOption[]>(
    () =>
      BONUS_TYPE_OPTIONS.map(type => ({
        value: type,
        label: type === ALL_OPTION_VALUE ? t('bonuses.ALL') : t(`bonuses.${type}`),
      })),
    [t],
  )

  const currencyOptions = useMemo<SelectOption[]>(() => {
    const currencies = Array.from(
      new Set([userCurrency, defaultCurrency, ...history.map(item => item.amountCurrency)].filter(Boolean)),
    )

    return [
      {
        value: ALL_OPTION_VALUE,
        label: t('bonuses.ALL'),
      },
      ...currencies.map(currency => ({
        value: currency,
        label: currency,
      })),
    ]
  }, [history, t, userCurrency])

  const formValuesRef = useRef<ExchangeBonusHistoryFilters | null>(null)

  const formik = useFormik<ExchangeBonusHistoryFilters>({
    initialValues: {
      periodStart: dayjs(new Date()).startOf('week').subtract(1, 'week').toDate(),
      periodEnd: new Date(),
      type: typeOptions[0],
      currency: currencyOptions[0],
    },
    onSubmit: values => {
      formValuesRef.current = values

      if (pagination.page !== 1) {
        pagination.onChangePage(1)
      }

      fetchBonusHistory(values)
    },
  })

  useEffect(() => {
    formValuesRef.current = formik.values
  }, [formik.values])

  const exchangeBonusFormik = useFormik<ExchangeBonusBalanceForm>({
    initialValues: {
      amount: '',
    },
    validationSchema,
    onSubmit: values => {
      let valid = true
      const valueNum = Number(values.amount || 0)

      if (valueNum < MIN_BONUS_BALANCE_AMOUNT) {
        addNotify(
          tPrefix('amountBonusLess', {
            currency: userCurrency,
          }),
        )
        valid = false
      }
      if (userInfo && +userInfo?.balance > bonusSettings.transferBonusBalanceToBalanceMinBalance!) {
        addNotify(
          tPrefix('balanceMore', {
            currency: userCurrency,
            value: bonusSettings.transferBonusBalanceToBalanceMinBalance,
          }),
        )
        valid = false
      }

      if (valid) {
        dispatch(
          exchangeBonusBalanceToBalance({ amount: valueNum }, { callback: () => fetchBonusHistory(formik.values) }),
        )
        formik.resetForm()
      }
    },
  })

  useEffect(() => {
    const values = formValuesRef.current

    if (values) {
      fetchBonusHistory(values)
    }
  }, [fetchBonusHistory, pagination.page])

  return {
    formik,
    count,
    pagination,
    history,
    fetchBonusHistory,
    bonusSettings,
    exchangeBonusFormik,
    typeOptions,
    currencyOptions,
    isLoading,
    error,
    isSuccess,
  }
}
